import { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { reportsAPI } from '../lib/api';
import { formatCurrency, formatApiError } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Loader2, TrendingUp, BarChart3, Sparkles, Users, Scissors, Package, CalendarDays, User } from 'lucide-react';
import { toast } from 'sonner';
import { exportToExcel } from '../lib/exportUtils';

const REPORT_OPTIONS = [
  { value: 'clients', label: 'Clientes frecuentes', icon: Users, valueLabel: 'Visitas', supportsOrder: true },
  { value: 'services', label: 'Servicios vendidos', icon: Scissors, valueLabel: 'Unidades', supportsOrder: true },
  { value: 'products', label: 'Productos vendidos', icon: Package, valueLabel: 'Unidades', supportsOrder: true },
  { value: 'weekdays', label: 'Días de la semana ocupados', icon: CalendarDays, valueLabel: 'Ventas', supportsOrder: true },
  { value: 'barbers', label: 'Barberos más solicitados', icon: User, valueLabel: 'Ventas', supportsOrder: true },
];

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };

export default function InsightsPage() {
  const { selectedBusiness } = useAuth();
  const [type, setType] = useState('clients');
  const [order, setOrder] = useState('desc');
  const [limit, setLimit] = useState(10);
  const [start, setStart] = useState(monthAgo());
  const [end, setEnd] = useState(today());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const currentOption = useMemo(() => REPORT_OPTIONS.find((o) => o.value === type) || REPORT_OPTIONS[0], [type]);

  const run = useCallback(async () => {
    if (!selectedBusiness) return;
    if (!start || !end) { toast.error('Selecciona fechas'); return; }
    setLoading(true);
    try {
      const { data } = await reportsAPI.insights(type, start, end, order, limit);
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err, 'No se pudo generar el reporte'));
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, type, start, end, order, limit]);

  useEffect(() => { run(); /* auto-run on mount */ }, [run]);

  const maxValue = useMemo(() => {
    if (!result?.rows?.length) return 0;
    return Math.max(...result.rows.map((r) => r.value || 0), 1);
  }, [result]);

  const orderLabel = order === 'desc' ? 'Más' : 'Menos';

  const exportExcel = () => {
    if (!result?.rows?.length) { toast.info('Nada que exportar'); return; }
    const rows = result.rows.map((r, i) => ({
      pos: i + 1,
      elemento: r.label,
      valor: r.value,
      ingreso: (r.revenue != null ? Number(r.revenue).toFixed(2) : ''),
    }));
    exportToExcel(`Insights_${type}_${start}_a_${end}`, [{
      name: currentOption.label.slice(0, 28),
      columns: [
        { header: '#', key: 'pos' },
        { header: 'Elemento', key: 'elemento' },
        { header: currentOption.valueLabel, key: 'valor' },
        { header: 'Ingreso', key: 'ingreso' },
      ],
      rows,
    }]);
    toast.success('Excel descargado');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="insights-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-blue-700" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>Reportes de análisis</h1>
          <p className="text-sm text-slate-500">Explora clientes, servicios, productos, días y barberos</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Configura el reporte</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-1.5">
              <Label>Tipo de reporte</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="insights-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REPORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Orden</Label>
              <Select value={order} onValueChange={setOrder}>
                <SelectTrigger data-testid="insights-order-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">De más a menos</SelectItem>
                  <SelectItem value="asc">De menos a más</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Desde</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="insights-start" />
            </div>
            <div className="space-y-1.5">
              <Label>Hasta</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="insights-end" />
            </div>
            <div className="space-y-1.5">
              <Label>Límite</Label>
              <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v, 10))}>
                <SelectTrigger data-testid="insights-limit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={exportExcel} disabled={loading || !result?.rows?.length}>
              Exportar Excel
            </Button>
            <Button onClick={run} disabled={loading} className="bg-blue-700 text-white hover:bg-blue-800" data-testid="insights-run-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
              Generar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Result */}
      <Card className="border-slate-200 bg-white">
        <CardHeader className="pb-3 flex flex-row items-center gap-2">
          <currentOption.icon className="w-5 h-5 text-slate-500" />
          <CardTitle className="text-base flex-1" style={{ fontFamily: 'Manrope' }}>
            {orderLabel} — {currentOption.label}
            {type !== 'weekdays' && (
              <span className="text-sm text-slate-400 font-normal ml-2">(Top {limit})</span>
            )}
          </CardTitle>
          {result && (
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5" /> {result.count || 0} resultados
            </span>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : !result?.rows?.length ? (
            <div className="py-12 text-center text-slate-500 text-sm">Sin datos en el período seleccionado</div>
          ) : (
            <div className="space-y-2" data-testid="insights-rows">
              {result.rows.map((row, idx) => {
                const barPct = maxValue > 0 ? Math.max(4, Math.round((row.value / maxValue) * 100)) : 0;
                return (
                  <div key={row.client_id || row.item_id || row.barber_id || row.label || idx} className="group">
                    <div className="flex items-center gap-3 text-sm">
                      <span className="w-6 shrink-0 text-slate-400 font-mono text-right">{idx + 1}.</span>
                      <span className="flex-1 truncate font-medium text-slate-800">{row.label}</span>
                      <span className="tabular-nums text-slate-600">
                        {row.value} {currentOption.valueLabel.toLowerCase()}
                      </span>
                      {row.revenue != null && (
                        <span className="tabular-nums text-slate-500 text-xs w-24 text-right">
                          {formatCurrency(row.revenue)}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 ml-9 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all"
                        style={{ width: `${barPct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
